/**
 * Wave 10 zod-mandate — runtime schemas for the server-verdict surface:
 * {@link BatchItemResult}, {@link BatchResponse} and the 0.4.0
 * {@link SyncConfirmation} event.
 *
 * `BatchResponse` is a genuine INBOUND boundary, not just an outbound type: a
 * custom `SyncQueueConfig.transport` hands one back to the engine, so its shape
 * is consumer-supplied and worth checking. Validation is warn-only (the lib
 * never throws on bad input) — a malformed verdict list still flows through
 * `applyResults`, where an unrecognised `client_event_uuid` is ignored and an
 * unmentioned entry is safely re-pended.
 *
 * @example
 * ```ts
 * import { BatchResponseSchema } from './Confirmation.schema'
 * BatchResponseSchema.parse({ results: [{ client_event_uuid: 'x', status: 'applied' }] })
 * ```
 */
import { z } from 'zod'

export const BatchItemStatusSchema = z.enum([
	'applied',
	'duplicate',
	'conflict',
	'rejected',
])

/** The two verdicts that mean "the server has this write". */
export const ConfirmedStatusSchema = z.enum(['applied', 'duplicate'])

// `passthrough` (not `strict`): a server is free to send extra per-item fields,
// and rejecting them would make the queue brittle against a benign API addition.
export const BatchItemResultSchema = z
	.object({
		client_event_uuid: z.string().min(1),
		status: BatchItemStatusSchema,
		server_row: z.unknown().optional(),
		server_version: z.number().optional(),
		message: z.string().optional(),
	})
	.passthrough()

export const BatchResponseSchema = z
	.object({
		results: z.array(BatchItemResultSchema),
	})
	.passthrough()

export const SyncConfirmationSchema = BatchItemResultSchema.extend({
	status: ConfirmedStatusSchema,
	entry: z.unknown(),
	table: z.string().min(1),
	operation: z.enum(['create', 'update', 'delete']),
	tempId: z.string().min(1).optional(),
	serverRow: z.unknown(),
	serverVersion: z.number().nullable(),
	confirmed_at: z.number(),
}).passthrough()

export type BatchItemStatusInput = z.infer<typeof BatchItemStatusSchema>
export type BatchItemResultInput = z.infer<typeof BatchItemResultSchema>
export type BatchResponseInput = z.infer<typeof BatchResponseSchema>
export type SyncConfirmationInput = z.infer<typeof SyncConfirmationSchema>
